import { Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useEffect } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import Images from "../../Images"
import LoginForm from "../../components/Forms/LoginForm"
import PrivacyPolicy from "../../components/PrivacyPolicy"
import { generateDocumentTitle } from "../../constants/document-title"
import { isHandledError } from "../../errors/handled-error"
import { presentHandledError } from "../../errors/present-handled-error"
import i18n from "../../i18n"
import type { RootState } from "../../store.ts"
import { handleSaveResult } from "../../utils/handlers/handle-save-result"
import { signedInLanding } from "./helpers/signed-in-landing.ts"
import { authErrorReset, authSetProp } from "./redux/auth.slice"
import { signIn, socialAuth } from "./redux/auth.thunks.ts"

const { Title, Paragraph } = Typography

function LoginPage({
  auth,
  error,
  fetching,
  socialAuth,
  setProp,
  signIn,
  resetError
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const { hash } = useParams()
  const { search } = useLocation()

  const otpRequired = error?.otp_required
  const formError = isHandledError(error)
    ? presentHandledError(error, (key, options) => i18n.t(key, options)).message
    : error?.message

  function handleSubmit({
    email,
    password,
    otp_pass = null
  }: {
    email: string
    password: string
    otp_pass?: string | null
  }) {
    const params = new URLSearchParams(search)
    const parsedParams = search ? Object.fromEntries(params.entries()) : null
    const nextUrl = search?.includes("?next=")
      ? search.split("?next=")?.[1]
      : null

    const data = {
      email,
      password,
      ...(otpRequired && { otp_pass }),
      ...(parsedParams && !parsedParams?.next && { extra: parsedParams }),
      ...(nextUrl && { redirect: nextUrl })
    }

    signIn({ data, navigate })
  }

  const handleSocialAuth = (data: any) => {
    socialAuth({ data, navigate })
  }

  useEffect(() => {
    generateDocumentTitle("Login")

    if (hash) localStorage.setItem("ref_link_info", hash)

    // Without a token PrivateRoute sends the user straight back here.
    if (auth.accessToken && auth.userInfo) {
      navigate(signedInLanding(auth.userInfo, search), { replace: true })
    }
    if (error) resetError()
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- runs once when the login page opens: it keeps the referral, sends a signed-in user on and clears a stale error
  }, [])

  useEffect(() => {
    if (auth?.saveResult?.type === "password_created") {
      handleSaveResult(auth?.saveResult)

      setProp({ key: "saveResult", value: null })
    }
  }, [auth?.saveResult, setProp])

  return (
    <Layout className={"min-h-svh auth-bg"}>
      <Spin size="large" spinning={fetching}>
        <Flex vertical align="center" justify="center" className={"min-h-svh"}>
          {/*<LoginHeader />*/}

          {error && (typeof error === "string" || otpRequired) && (
            <div className={"force-logout-error"}>
              {typeof error === "string" ? error : "OTP is required"}
            </div>
          )}

          <Card
            className="login-card shadow-sm border-gray-200/70"
            style={{ margin: "auto", width: "100%", maxWidth: "520px" }}
          >
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <img src={Images.logo} alt="Logo" className="w-44 h-28" />

              <Title level={1} style={{ marginBottom: 6 }}>
                Log in to account
              </Title>
              <Paragraph
                type="secondary"
                className={"text-center"}
                style={{ fontSize: 16, margin: 0 }}
              >
                Welcome back! Please enter your details.
              </Paragraph>
            </Flex>

            <LoginForm
              onSubmit={handleSubmit}
              error={formError}
              otpRequired={otpRequired}
              resetError={resetError}
              socialAuth={handleSocialAuth}
            />
          </Card>
          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  error: state.auth.error,
  fetching: state.auth.fetching
})

const mapDispatchToProps = {
  signIn,
  socialAuth,
  resetError: authErrorReset,
  setProp: authSetProp
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(LoginPage)
